import { getOrCreateMainLogger } from "../logger/main";
import { getURL, isDevLaunch } from "../util";
import { download, DownloadOptions } from "./download";
import { Manifest } from "../types/types";
import merge from "deepmerge";
import parseArgs from "minimist";
import { set, get } from "lodash";
import { defaultConfig, getConfig } from "./Config";

const logger = getOrCreateMainLogger();

/**
 * "globalManifest" will contain the manifest. This is exported as Manifest.manifest, so that it can
 * be accessed globally. Ideally at some point we refactor so that we don't need access to a global.
 */
let globalManifest = {};

/**
 * If we're running in dev mode then we can pass the manifest location with a command line
 * argument or url parameter.
 */
const getManifestURLFromArgument = () => {
	const args = parseArgs(process.argv);
	let manifestURL: string | null = args["manifest"];
	if (manifestURL) {
		logger.debug(`Manifest URL from "--manifest" arg`);
		return manifestURL;
	}

	// looking for the manifest url passed in the protocol handler
	process.argv.forEach((arg) => {
		if (arg.startsWith("fsbl://manifest/")) {
			let pathName = new URL(arg).pathname;
			// remove leading slash
			if (pathName.startsWith("/")) {
				pathName = pathName.substring(1);
			}
			// if the protocol URL is complex cut the part with protocol url
			const slashIndex = pathName.indexOf("/");
			if (slashIndex > 0) {
				pathName = pathName.substring(0, slashIndex);
			}

			manifestURL = decodeURIComponent(pathName);
			logger.debug(`Manifest URL from the protocolURL`, manifestURL);
		}
	});

	return manifestURL;
};

/**
 * Fetches the manifest from the given manifestURL.
 * @param manifestURL string The URL where the manifest can be fetched
 * @param [options] DownloadOptions Accepts the same options as download() (See ../common/download.ts)
 */
const getManifestFromUrl = async (manifestURL: string, options?: DownloadOptions) =>
	new Promise<object>(async (resolve, reject) => {
		logger.info(`Fetching manifest URL: ${manifestURL}`);
		let opts = { ...options };

		// Prevent caching of manifests
		if (!opts.headers) opts.headers = {};
		opts.headers["Cache-Control"] = "no-cache";

		let manifestText: Buffer;
		try {
			({ data: manifestText } = await download(manifestURL, opts));
		} catch (err: any) {
			const error = `We couldn't receive the manifest from server. 
			This is likely due to a connectivity issue. 
			Check your network environment (VPN connection, ISP authentication, NAC, firewall, 
			antivirus and other settings) and try again.\n\nError: ${err.message || err}`;
			return reject(error);
		}

		logger.info(`getManifestFromUrl(). Full manifest: ${manifestText}`);
		try {
			const manifest = JSON.parse(manifestText.toString());
			return resolve(manifest);
		} catch (err: any) {
			const error = `Error parsing manifest ${manifestURL} : ${err.message || err}`;
			return reject(error);
		}
	});

/**
 * "Runtime" manifests can be passed to FEA either as command line arguments or through a
 * protocol handler. These runtime manifests are subject to whitelist restrictions that
 * may be embedded in the startup manifest.
 *
 * See deployingYourSmartDesktop.md for documentation on runtime manifests.
 *
 * Resolves to a string if there's a valid run time manifest. Resolves to null if there isn't.
 * Rejects to a string error if there's any attempt to set an invalid runtime manifest.
 */
const getRuntimeManifestURL = async (startupManifest: any) =>
	// eslint-disable-next-line sonarjs/cognitive-complexity
	new Promise<string | null>((resolve, reject) => {
		const args: any = parseArgs(process.argv);
		const feaConfig: any = startupManifest["finsemble-electron-adapter"];

		/**
		 * Electron passes protocol handlers as two command line arguments. Therefore, to
		 * determine if we've been called by a protocol handler, we need to ensure that there
		 * are two arguments and the second argument is a valid URL.
		 */
		const protocolURL = process.argv.length === 2 ? getURL(process.argv[1]) : null;

		logger.debug("getRuntimeManifestURL() args:", JSON.stringify(process.argv));
		logger.debug("getRuntimeManifestURL() protocolURL:", protocolURL);
		logger.debug("getRuntimeManifestURL() startupManifest:", JSON.stringify(startupManifest, null, 4));

		/**
		 * If we have a protocolURL then get the hostname. If the hostname is "manifest" or "manifest-url"
		 * then we have a runtime manifest. Note, we check for protocolURL.hostname to exist just in case
		 * a string is passed instead of a URL.
		 */
		const protocolHostName = protocolURL?.hostname.toLowerCase() ?? null;

		/**
		 * Get runtime manifest by name. The name is passed either as a command line argument `--manifest-name frank`
		 * or by protocol handler `fsbl://manifest/frank`. In either case, the name must be found in the "manifests"
		 * section of the startup config in order to map the name to a url.
		 */
		let manifestName: string | null = null;
		if (args["manifest-name"]) manifestName = args["manifest-name"];
		if (protocolHostName === "manifest" && protocolURL) {
			manifestName = protocolURL.pathname.replace("//manifest/", "");
			if (manifestName.startsWith("/")) manifestName = manifestName.substring(1); // remove leading slash
		}

		/**
		 * Get *runtime* manifest url. The url can be passed either as a command line
		 * argument `--manifest-url http://blah/etc` or by protocol handler `fsbl://manifest-url/http://blah/etc`.
		 * URLs are likely to be url encoded, so we pass them through a decoder.
		 */

		const maybeGetRuntimeManifestURL = () => {
			let runtimeManifestURLString;
			if (args["manifest-url"]) runtimeManifestURLString = args["manifest-url"];
			if (protocolHostName === "manifest-url" && protocolURL) {
				runtimeManifestURLString = protocolURL.pathname.replace("//manifest-url/", "");
				if (runtimeManifestURLString.startsWith("/")) runtimeManifestURLString = runtimeManifestURLString.substring(1); // remove leading slash
			}
			if (runtimeManifestURLString) runtimeManifestURLString = decodeURIComponent(runtimeManifestURLString);
			return runtimeManifestURLString;
		};

		const runtimeManifestURLString = manifestName ? feaConfig.manifests[manifestName] : maybeGetRuntimeManifestURL();

		// If there's a runtime manifest url, then ensure that it is valid
		const runtimeManifestURL = getURL(runtimeManifestURLString);
		if (runtimeManifestURLString && !runtimeManifestURL) {
			const error = `Invalid runtime manifest URL was specified: "${runtimeManifestURL}"`;
			return reject(error);
		}

		// If there's a runtime manifest url, then ensure that it is allowed
		// Directly requested manifest urls must be in the the `allowedManifestDomains` config of the manifest
		// "*" will allow any requested domain
		if (!manifestName && runtimeManifestURL) {
			const domains = feaConfig.allowedManifestDomains;
			if (!domains.includes(runtimeManifestURL.hostname) && !domains.includes("*")) {
				const error = `Manifest URL ${runtimeManifestURL} is not allowed. Check "allowedManifestDomains" setting in startup manifest`;
				return reject(error);
			}
		}
		if (runtimeManifestURL) {
			logger.debug("Runtime manifest URL:", runtimeManifestURL.toString());
			return resolve(runtimeManifestURL.toString());
		}
		return resolve(null);
	});

/**
 * A very rudimentary validator to help with the most basic manifest problems.
 * @param manifest any
 */
const verifyManifest = async (manifest: any) =>
	new Promise<void>((resolve, reject) => {
		let error: string | null = null;

		if (!manifest) {
			error = "No manifest provided.";
		} else if (typeof manifest.startup_app !== "object") {
			error = "Illegal Manifest: manifest.startup_app is not a defined object.";
		} else if (typeof manifest.startup_app.url !== "string") {
			error = "Illegal Manifest: manifest.startup_app.url is not a defined string.";
		}

		if (error) {
			reject(error);
		} else {
			resolve();
		}
	});

/**
 * Returns an absolute URL given a url string that is relative to the location of the manifest. This
 * function depends on the fact that Manifest has been amended with `finsemble.manifestURL` in
 * Manifest.ts.
 *
 * If the urlString is an absolute path then it will be returned unmodified.
 * @param {string} baseURL - The base URL (e.g. the manifest location)
 * @param {string} urlString - The url to resolve
 */
const resolveToAbsoluteURL = (baseURL: string, urlString: string): string => {
	let result = urlString;
	// If the urlString starts with "$" then don't process because it's using an import variable
	if (typeof urlString === "string" && urlString?.length && urlString.charAt(0) != "$") {
		try {
			// Converts a relative path to the absolute path of baseURL (only if urlString isn't already absolute path)
			result = new URL(urlString, baseURL).href;

			// Replace extraneous duplicate slashes (except for the one in the protocol http://)
			result = result.replace(/([^:]\/)\/+/g, "$1");

			// Remove trailing slashes
			if (result.slice(-1)[0] === "/") result = result.slice(0, -1);
		} catch (err) {}
	}
	return result;
};

type TraverseItem = {
	_path: string[];
	obj: any;
	key: string;
	value: any;
};

/**
 * Utility function to recursively traverse a nested object and run an iterator function for each node.
 *
 * This does handle arrays (they are type "object").
 *
 * @param {any} obj - The object to iterate
 * @param {(TraverseItem) => void} iterator - The iterator function which gets called on any node
 */
const traverse = (obj: any, iterator: (item: TraverseItem) => void, _path: string[] = []) => {
	if (!obj || typeof obj !== "object") return;
	Object.entries(obj).forEach((entry) => {
		const [key, value] = entry;
		const newPath = _path.concat([key]);
		if (typeof value === "object") {
			traverse(value, iterator, newPath);
		} else {
			iterator({ _path: newPath, obj, key, value });
		}
	});
};

/**
 * Ensures that the manifest has everything necessary for FEA to start.
 * This includes ensuring that the "finsemble-electron-adapter" section is filled in.
 * It also converts entries with relative urls to absolute urls.
 * @param manifest object The manifest
 * @param manifestURL string The URL of the manifest. This is used to convert the manifest to absolute paths.
 * @param [appendImports] Array<string> Optional additional import files to inject. Used to add the smart desktop designer component and workspace.
 */
export const normalizeManifest = (
	manifest: Manifest,
	manifestURL: string,
	appendImports: Array<string>,
	appName: string
) => {
	const DefaultFEAConfig = {
		manifests: {},
		allowedManifestDomains: [],
		logger: { transports: { console: { enable: false } } },
	};

	manifest["finsemble-electron-adapter"] = merge(DefaultFEAConfig, manifest["finsemble-electron-adapter"] || {});

	/**
	 * takes any variables inside `env-allowlist` and adds
	 * the key/value pair to finsemble.custom so that user's
	 * environment variables are set and usable in the manifest
	 **/
	manifest["finsemble-electron-adapter"]["env-allowlist"]?.forEach((enVar: string) => {
		manifest.finsemble.custom[enVar] = process.env[enVar];
	});
	// Ensure static defaults for startup_app.
	manifest.startup_app = {
		...manifest.startup_app,
		name: manifest.startup_app?.name ?? appName,
		uuid: manifest.startup_app?.uuid ?? "Finsemble",
	};

	/**
	 * These are all the possible manifest entries that may need path resolution.
	 * The value for each key is the default value if the entry is missing. Entries
	 * with null do not have a default value.
	 **/
	const resolvableManifestEntries = {
		splashScreenImage: "$documentRoot/assets/img/FinsembleSplash.png",
		finsemble: {
			documentRoot: "../../",
			applicationRoot: "$documentRoot/build",
			configRoot: "$documentRoot/configs",
			moduleRoot: "$applicationRoot/finsemble",
			servicesRoot: "$moduleRoot/services",
			configSchemaURL: "$moduleRoot/configs/schemas/finsemble.schema.json",
			systemTrayIcon: "$documentRoot/assets/img/Finsemble_SystemTray_Icon.png",
			// Default location of the window title bar. This will be overridden if there is a component
			// entry for WindowTitleBar/windowTitleBar.
			windowTitleBarUrl: "$moduleRoot/ui-components/WindowTitleBar",
		},
		startup_app: {
			url: "$servicesRoot/systemManager/systemManager.html",
			applicationIcon: "$documentRoot/assets/img/Finsemble_Taskbar_Icon.png",
		},
		affinitySurrogateUrl: "$servicesRoot/systemManager/affinity-agent.html",
	};

	let macros: {
		[macro: string]: string;
	} = {};

	/**
	 * Adds any missing default manifest entries (from the resolvableManifestEntries section).
	 * Then, for all of those entries, any relative paths are converted to absolute paths based on the
	 * location of the manifest file. (macros are replaced later)
	 */
	traverse(resolvableManifestEntries, ({ _path, value: defaultValue }) => {
		const value = get(manifest, _path) ?? defaultValue;
		if (typeof value === "string") {
			const resolvedValue = resolveToAbsoluteURL(manifestURL, value);
			set(manifest, _path, resolvedValue);
			const macro = `$${_path.slice(-1)[0]}`;
			macros[macro] = resolvedValue;
		}
	});

	/**
	 * Replaces macros with their values in a string. This includes macros that reference other macros.
	 *
	 * @param {string} value The string to replace macros
	 * @returns A string with all macros replaced
	 */
	const replaceMacros = (value: string) => {
		let resolvedValue = value;
		if (typeof value === "string") {
			const infiniteLoopProtection = Object.entries(macros).length;
			for (let i = 0; i < infiniteLoopProtection; i++) {
				let madeReplacement = false;
				Object.entries(macros).forEach((entry) => {
					const [macro, macroValue] = entry;
					if (resolvedValue.includes(macro)) {
						resolvedValue = resolvedValue.replace(macro, macroValue);
						madeReplacement = true;
					}
				});
				if (!madeReplacement) break;
			}
		}
		return resolvedValue;
	};

	/**
	 * Macros are replaced for the entire manifest file. Since macros can reference
	 * other macros, the manifest is traversed multiple times.
	 */
	traverse(manifest, ({ obj, key, value }) => {
		if (typeof value === "string") {
			const resolvedValue = replaceMacros(value);
			obj[key] = resolvedValue;
		}
	});

	// importConfig is an array. First add any injected import files (appendImports, passed from app.js). Then replace macros and resolve everything to absolute urls.
	const { importConfig } = manifest.finsemble;
	const appendedImportConfig = (importConfig ? importConfig : []).concat(appendImports);
	manifest.finsemble.importConfig = appendedImportConfig.map((url: string) =>
		resolveToAbsoluteURL(manifestURL, replaceMacros(url))
	);
};

type GetManifestParams = {
	manifestURL?: string | null;
	options?: DownloadOptions;
	appendImports: Array<string>;
	appName: string;
};

/**
 * Logic to fetch the manifest url. When in dev mode, this will always return a manifest location, either from the command
 * line or the default location.
 *
 * When running in production mode, if the manifest is passed on the command line then it will be used, otherwise it will
 * look in the asar file. If it cannot be found in either location then an exception is thrown which results in a user
 * prompt to display the error.
 */
const getManifestURL = async (): Promise<string> => {
	if (isDevLaunch()) {
		const url = getManifestURLFromArgument();
		if (!url)
			logger.error(`No "--manifest" argument found. Using default manifest location: ${defaultConfig.manifestUrl}`);
		return url ?? defaultConfig.manifestUrl;
	} else {
		return getManifestURLFromArgument() ?? getConfig().manifestUrl;
	}
};

/**
 * Gets the manifest for FEA. This will resolve from several potential places
 * including the embedded manifest location for production install builds, or from command
 * line arguments in dev mode, or it can be passed in such as from a location determined by smart desktop server.
 *
 * Rejects from subfunctions will resolve to an error string. app.js will catch
 * all errors for log and dialog.
 * @param [params.manifestURL] string Optionally provide the manifest URL, otherwise it will be determined automatically
 * @param [params.options] DownloadOptions Same options from download(). See ../common/download.ts
 * @param [params.appendImports] Optional array of additional import files to append into the manifest's import section. This is used by smart desktop server.
 */
const getManifest = async ({ manifestURL = null, options, appendImports, appName }: GetManifestParams) =>
	new Promise<object>(async (resolve, reject) => {
		try {
			logger.debug("Manifest.getManifest()");
			const startupURL = manifestURL ?? (await getManifestURL());

			const opts = { ...options, logger };
			logger.debug("Manifest.getManifest(): Initial manifest URL: ", startupURL);
			let startupManifest = await getManifestFromUrl(startupURL, opts);
			normalizeManifest(startupManifest, startupURL, appendImports, appName);
			let manifest = startupManifest;

			const runtimeManifestURL = await getRuntimeManifestURL(startupManifest);
			if (runtimeManifestURL) {
				manifest = await getManifestFromUrl(runtimeManifestURL);
				normalizeManifest(manifest, runtimeManifestURL, appendImports, appName);
			}

			await verifyManifest(manifest);

			globalManifest = manifest;
			resolve(manifest);
		} catch (error) {
			reject(error);
		}
	});

/**
 * Simple getter function for the current global manifest.
 */
const getGlobalManifest = () => globalManifest;

export { getManifest, getGlobalManifest as manifest, resolveToAbsoluteURL };
